import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldAlert, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AuthShell, Field, PasswordInput, btnPrimary } from "@/components/AuthShell";

const rules = [
  { label: "Minimal 8 karakter", test: (v) => v.length >= 8 },
  { label: "Ada huruf besar & kecil", test: (v) => /[a-z]/.test(v) && /[A-Z]/.test(v) },
  { label: "Mengandung angka", test: (v) => /\d/.test(v) },
];

export default function ChangeInitialPassword() {
  const { refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (ev) => {
    ev.preventDefault();
    setError("");
    if (newPw !== confirm) {
      setError("Konfirmasi kata sandi baru tidak cocok.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      await refresh();
      toast.success("Kata sandi berhasil diperbarui.");
      navigate("/dashboard", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Pembaruan Kata Sandi Wajib"
      subtitle="Untuk keamanan akun perdana Anda, wajib membuat kata sandi baru yang aman."
    >
      <div className="mb-5 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
        <ShieldAlert className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-800">
          Kata sandi bawaan sistem harus diganti sebelum Anda dapat mengakses modul CRM.
        </p>
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="firstlogin-form">
        <Field label="Kata Sandi Saat Ini">
          <PasswordInput required value={oldPw} onChange={(e) => setOldPw(e.target.value)} placeholder="Masukkan kata sandi sementara" testId="firstlogin-old-password-input" />
        </Field>
        <Field label="Kata Sandi Baru">
          <PasswordInput required value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Minimal 8 karakter (huruf & angka)" testId="firstlogin-new-password-input" />
        </Field>
        <Field label="Konfirmasi Kata Sandi Baru">
          <PasswordInput required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Ulangi kata sandi baru" testId="firstlogin-confirm-password-input" />
        </Field>

        <ul className="space-y-1">
          {rules.map((r) => {
            const ok = r.test(newPw);
            return (
              <li key={r.label} className={`flex items-center gap-2 text-xs ${ok ? "text-green-600" : "text-slate-400"}`}>
                <Check className="h-3.5 w-3.5" /> {r.label}
              </li>
            );
          })}
        </ul>

        {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="firstlogin-error">{error}</div>}

        <button type="submit" disabled={loading} className={btnPrimary} data-testid="firstlogin-submit-button">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Simpan & Lanjut ke Dashboard
        </button>
        <button
          type="button"
          onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
          className="w-full text-sm text-slate-500 hover:text-slate-800"
          data-testid="firstlogin-cancel-button"
        >
          Batal & Keluar
        </button>
      </form>
    </AuthShell>
  );
}
